import { useState } from "react";
import {
  BODY_MEASUREMENT_CATALOG,
  type BodyMeasurementTypeKey,
} from "@/lib/body-measurements";
import type {
  CycleRecord,
  CycleTarget,
  JudgingRule,
  PointsTarget,
} from "@/lib/cycles";
import { updateCycle } from "@/lib/repos/cycles";
import { Modal } from "./Modal";

/**
 * What a Points target can point at: a whole Summary row, or a widget
 * instance or part. `value` is the picker's key — `row:{rowId}` or
 * `widget:{ref}` — so the two kinds never collide.
 */
export interface TargetCandidate {
  group: "row" | "widget";
  value: string;
  label: string;
}

function candidateValueOf(target: PointsTarget): string {
  return target.summaryRowId !== undefined
    ? `row:${target.summaryRowId}`
    : `widget:${target.ref ?? ""}`;
}

const GROUP_LABEL: Record<TargetCandidate["group"], string> = {
  row: "Rader i ukeoversikten",
  widget: "Enkeltwidgets",
};

/** Mounted only while open, so the form starts fresh each time. */
interface Props {
  cycle: CycleRecord;
  candidates: TargetCandidate[];
  /** Absent: a new target. Present: edit that one. */
  target?: CycleTarget;
  onClose: () => void;
}

type Kind = CycleTarget["kind"];

/** "−2,5" or "-2.5" → -2.5; null when not a number. */
function parseSigned(raw: string): number | null {
  const normalized = raw.trim().replace(",", ".").replace("−", "-");
  if (normalized === "") return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

export function CycleTargetModal({
  cycle,
  candidates,
  target,
  onClose,
}: Props) {
  const points = target?.kind === "points" ? target : null;
  const measurement = target?.kind === "measurement" ? target : null;

  const [kind, setKind] = useState<Kind>(target?.kind ?? "points");
  const [source, setSource] = useState(
    points ? candidateValueOf(points) : (candidates[0]?.value ?? ""),
  );
  const [requiredPercent, setRequiredPercent] = useState(
    String(points?.requiredPercent ?? 80),
  );
  const [rule, setRule] = useState<JudgingRule>(points?.rule ?? "samlet");
  const [requiredWeeks, setRequiredWeeks] = useState(
    String(points?.requiredWeeks ?? cycle.data.length),
  );
  const [typeKey, setTypeKey] = useState<BodyMeasurementTypeKey>(
    measurement?.typeKey ?? "weight",
  );
  const [wantedChange, setWantedChange] = useState(
    measurement ? String(measurement.wantedChange).replace(".", ",") : "",
  );
  const [saveError, setSaveError] = useState<string | null>(null);

  const unit =
    BODY_MEASUREMENT_CATALOG.find((t) => t.key === typeKey)?.unit ?? "";

  function build(): CycleTarget | string {
    const id = target?.id ?? crypto.randomUUID();
    if (kind === "measurement") {
      const change = parseSigned(wantedChange);
      if (change === null || change === 0) {
        return "Skriv ønsket endring, f.eks. -3.";
      }
      return { id, kind, typeKey, wantedChange: change };
    }
    if (!source) return "Velg hva målet gjelder.";
    const percent = Number(requiredPercent);
    if (!Number.isFinite(percent) || percent <= 0) {
      return "Prosenten må være over 0.";
    }
    const built: PointsTarget = source.startsWith("row:")
      ? {
          id,
          kind,
          summaryRowId: source.slice("row:".length),
          requiredPercent: percent,
          rule,
        }
      : {
          id,
          kind,
          ref: source.slice("widget:".length),
          requiredPercent: percent,
          rule,
        };
    if (rule === "uke-for-uke") {
      const weeks = Number(requiredWeeks);
      if (!Number.isInteger(weeks) || weeks < 1 || weeks > cycle.data.length) {
        return `Antall uker må være mellom 1 og ${cycle.data.length}.`;
      }
      built.requiredWeeks = weeks;
    }
    return built;
  }

  const result = build();
  const validation = typeof result === "string" ? result : null;
  const error = validation ?? saveError;

  const save = async () => {
    if (typeof result === "string") return;
    try {
      await updateCycle(cycle.id, (prev) => ({
        ...prev,
        targets: target
          ? prev.targets.map((t) => (t.id === target.id ? result : t))
          : [...prev.targets, result],
      }));
      onClose();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Modal open onClose={onClose} data-testid="cycle-target-modal">
      <h2>{target ? "Rediger mål" : "Nytt mål"}</h2>
      <div className="cycle-form">
        {target ? null : (
          <fieldset className="seg" aria-label="Type mål">
            <button
              type="button"
              className={kind === "points" ? "on" : undefined}
              onClick={() => setKind("points")}
              data-testid="target-kind-points"
            >
              Poeng
            </button>
            <button
              type="button"
              className={kind === "measurement" ? "on" : undefined}
              onClick={() => setKind("measurement")}
              data-testid="target-kind-measurement"
            >
              Kroppsmål
            </button>
          </fieldset>
        )}

        {kind === "points" ? (
          <>
            <label className="setting-label" htmlFor="target-ref">
              Gjelder
            </label>
            {candidates.length === 0 ? (
              <p className="hint" data-testid="target-no-candidates">
                Ingen rader eller widgets som gir poeng i ukesmalen eller i
                syklusens uker.
              </p>
            ) : (
              <select
                id="target-ref"
                className="input"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                data-testid="target-ref-select"
              >
                {(["row", "widget"] as const).map((group) => {
                  const inGroup = candidates.filter((c) => c.group === group);
                  if (inGroup.length === 0) return null;
                  return (
                    <optgroup key={group} label={GROUP_LABEL[group]}>
                      {inGroup.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
            )}

            <label className="setting-label" htmlFor="target-percent">
              Krav <small>Andel av ukemålet.</small>
            </label>
            <div className="num-input">
              <input
                id="target-percent"
                type="number"
                min={1}
                value={requiredPercent}
                onChange={(e) => setRequiredPercent(e.target.value)}
                data-testid="target-percent-input"
              />
              <span className="unit">%</span>
            </div>

            <span className="setting-label">
              Vurderes
              <small>
                {rule === "samlet"
                  ? "Alle ukene lagt sammen — en sterk uke kan dekke for en svak."
                  : "Hver uke for seg — teller hvor mange uker som når kravet."}
              </small>
            </span>
            <fieldset className="seg" aria-label="Vurderes">
              <button
                type="button"
                className={rule === "samlet" ? "on" : undefined}
                onClick={() => setRule("samlet")}
                data-testid="target-rule-samlet"
              >
                Samlet
              </button>
              <button
                type="button"
                className={rule === "uke-for-uke" ? "on" : undefined}
                onClick={() => setRule("uke-for-uke")}
                data-testid="target-rule-uke-for-uke"
              >
                Uke for uke
              </button>
            </fieldset>

            {rule === "uke-for-uke" ? (
              <>
                <label className="setting-label" htmlFor="target-weeks">
                  Uker som må nå kravet
                </label>
                <div className="num-input">
                  <input
                    id="target-weeks"
                    type="number"
                    min={1}
                    max={cycle.data.length}
                    value={requiredWeeks}
                    onChange={(e) => setRequiredWeeks(e.target.value)}
                    data-testid="target-weeks-input"
                  />
                  <span className="unit">av {cycle.data.length}</span>
                </div>
              </>
            ) : null}
          </>
        ) : (
          <>
            <label className="setting-label" htmlFor="target-type">
              Måling
            </label>
            <select
              id="target-type"
              className="input"
              value={typeKey}
              onChange={(e) =>
                setTypeKey(e.target.value as BodyMeasurementTypeKey)
              }
              data-testid="target-type-select"
            >
              {BODY_MEASUREMENT_CATALOG.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>

            <label className="setting-label" htmlFor="target-change">
              Ønsket endring
              <small>
                Over syklusen, fra snittet uken før. Minus betyr ned.
              </small>
            </label>
            <div className="num-input">
              <input
                id="target-change"
                type="text"
                inputMode="decimal"
                value={wantedChange}
                onChange={(e) => setWantedChange(e.target.value)}
                data-testid="target-change-input"
              />
              <span className="unit">{unit}</span>
            </div>
          </>
        )}
      </div>
      {error ? (
        <p className="error" data-testid="cycle-target-error">
          {error}
        </p>
      ) : null}
      <div className="modal-actions">
        <button
          type="button"
          className="btn ghost"
          onClick={onClose}
          data-testid="target-cancel-button"
        >
          Avbryt
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={save}
          disabled={validation !== null}
          data-testid="target-save-button"
        >
          Lagre
        </button>
      </div>
    </Modal>
  );
}
